/**
 * The gate every screen that calls an authenticated endpoint sits behind -
 * the submit form and the reconciliation table (phase 6), not Diagnostics,
 * which already has its own bespoke rendering of every `Session` state to
 * prove the sign-in flow itself (phase 4 doc, section 6) and is left alone.
 *
 * Renders `children(getToken)` only once the session can actually produce a
 * token (`mock`, where none is needed, or `signed-in`); every other state
 * renders its own explanation instead, mirroring `Diagnostics.tsx`'s existing
 * copy for the same states so the message is consistent wherever it appears.
 */
import type { ReactNode } from "react";

import { tokenGetterFor, useSession, type TokenGetter } from "../auth/session";

export function SessionGate({ children }: { children: (getToken: TokenGetter) => ReactNode }) {
  const session = useSession();

  if (session.kind === "loading") {
    return <p className="muted">Reading backend configuration…</p>;
  }

  if (session.kind === "error") {
    return (
      <section className="card error">
        <h2>Cannot reach the backend</h2>
        <p>{session.message}</p>
      </section>
    );
  }

  if (session.kind === "signing-in") {
    return <p className="muted">Completing sign-in…</p>;
  }

  if (session.kind === "unconfigured") {
    return (
      <section className="card error">
        <h2>Sign-in is not available</h2>
        <p>
          The backend is in <code>entra</code> mode but <code>/auth/config</code> returned no
          client id, so there is nothing to initialise sign-in with. Set{" "}
          <code>AUTH_API_CLIENT_ID</code> on the backend.
        </p>
      </section>
    );
  }

  if (session.kind === "signed-out") {
    return (
      <section className="card">
        <h2>Sign in</h2>
        <p>Sign in with your @cubanbread.com account to continue.</p>
        <button type="button" onClick={() => void session.signIn()}>
          Sign in
        </button>
      </section>
    );
  }

  const getToken = tokenGetterFor(session);
  // Exhaustive given the branches above (mock | signed-in are what's left),
  // but TypeScript can't see that through the helper - the null check is
  // otherwise dead code.
  if (!getToken) return null;
  return <>{children(getToken)}</>;
}
